import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { useAdminContext } from '../session'

type JobKind = 'refresh' | 'reset'
type JobStatus = 'pending' | 'running' | 'succeeded' | 'failed'

interface TestEnvJob {
  id: string
  kind: JobKind
  status: JobStatus
  requested_by_label: string
  requested_at: string
  started_at: string | null
  heartbeat_at: string | null
  finished_at: string | null
  prod_version: string | null
  test_version: string | null
  stale: boolean
}

interface TestEnvJobDetail extends TestEnvJob {
  log_tail: string | null
}

interface TestEnvOverview {
  runner: {
    configured: boolean
    online: boolean
    last_seen_at: string | null
    prod_version: string | null
    test_version: string | null
    test_healthy: boolean | null
    test_url: string | null
    test_copied_at: string | null
  }
  active_job: TestEnvJobDetail | null
  last_finished_job: TestEnvJobDetail | null
  jobs: TestEnvJob[]
}

const OVERVIEW_KEY = 'admin-test-env'

/**
 * Le système de test, piloté depuis la production.
 *
 * Deux boutons, et tout ce qu'il faut savoir avant d'appuyer :
 *
 * * **Actualiser** — le test passe sur la version qui tourne en production,
 *   puis repart d'une copie fraîche de ses données. Pour répéter sur ce que
 *   les clients voient réellement aujourd'hui.
 * * **Réinitialiser** — le test garde son code (une version en cours d'essai,
 *   par exemple) et repart d'une copie fraîche des données.
 *
 * Les deux effacent tout ce que les testeurs ont fait. D'où la confirmation
 * qui le dit en toutes lettres, et l'état affiché en haut : on ne copie pas
 * sans savoir sur quelle version on atterrit.
 *
 * Rien ne s'exécute dans le navigateur ni dans l'API : un clic **dépose une
 * demande**, qu'un lanceur posé sur le serveur vient chercher dans la minute.
 * L'écran se relit toutes les cinq secondes tant qu'une demande vit, et
 * montre la fin de son journal — c'est là qu'on lit pourquoi une copie a
 * échoué, sans ouvrir de terminal.
 *
 * Sur le site de test lui-même, ces boutons n'ont pas de sens (copier le test
 * sur lui-même) : le serveur refuse, et l'écran explique où aller.
 */
export function AdminTestSystemPage() {
  const { t } = useTranslation()
  const { eventId } = useAdminContext()
  const [confirming, setConfirming] = useState<JobKind | null>(null)

  useEffect(() => {
    document.title = t('admin.section.testSystem')
  }, [t])

  const overview = useAdminQuery<TestEnvOverview>([OVERVIEW_KEY], '/admin/test-env', eventId, {
    // Cinq secondes tant qu'une demande vit ; sinon une fois la minute, pour
    // voir passer le lanceur et changer les versions.
    refetchInterval: (query) => (query.state.data?.active_job ? 5_000 : 60_000),
    // Un 409 « pas la production » n'a rien de passager : le réessayer ne
    // ferait que retarder l'explication.
    retry: false,
  })

  const create = useAdminMutation<TestEnvJobDetail, { kind: JobKind }>(
    'post',
    () => '/admin/test-env/jobs',
    eventId,
    [[OVERVIEW_KEY]],
  )
  const cancel = useAdminMutation<TestEnvJobDetail, { id: string }>(
    'post',
    ({ id }) => `/admin/test-env/jobs/${id}/cancel`,
    eventId,
    [[OVERVIEW_KEY]],
  )

  const header = (
    <AdminHeader title={t('admin.section.testSystem')} description={t('admin.testSystem.intro')} />
  )

  if (overview.isPending) {
    return (
      <>
        {header}
        <Skeleton className="h-72 w-full" />
      </>
    )
  }

  if (overview.isError) {
    const notProduction =
      overview.error instanceof ApiError && overview.error.code === 'TEST_ENV_PRODUCTION_ONLY'
    return (
      <>
        {header}
        {notProduction ? (
          <Card className="max-w-2xl">
            <h2 className="font-display text-heading-md text-content-primary">
              {t('admin.testSystem.notProductionTitle')}
            </h2>
            <p className="mt-2 text-sm text-content-secondary">
              {t('admin.testSystem.notProductionBody')}
            </p>
          </Card>
        ) : (
          <p className="text-sm text-state-danger">{overview.error.message}</p>
        )}
      </>
    )
  }

  const data = overview.data
  const { runner } = data
  const busy = data.active_job !== null
  const sameVersion =
    runner.prod_version !== null &&
    runner.test_version !== null &&
    runner.prod_version === runner.test_version
  const lastCopy =
    runner.test_copied_at ??
    data.jobs.find((job) => job.status === 'succeeded')?.finished_at ??
    null

  return (
    <>
      {header}

      <StatusCard overview={data} sameVersion={sameVersion} lastCopy={lastCopy} />

      <Card className="mt-6">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.testSystem.actions.title')}
        </h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {(['refresh', 'reset'] as const).map((kind) => (
            <div
              key={kind}
              className="flex flex-col gap-3 rounded-md border border-line-subtle p-4"
            >
              <p className="text-sm text-content-secondary">
                {t(`admin.testSystem.actions.${kind}.description`)}
              </p>
              <Button
                className="mt-auto w-full sm:w-auto sm:self-start"
                variant={kind === 'refresh' ? 'ember' : 'outline'}
                disabled={busy || !runner.configured || create.isPending}
                onClick={() => {
                  create.reset()
                  setConfirming(kind)
                }}
              >
                {t(`admin.testSystem.actions.${kind}.label`)}
              </Button>
            </div>
          ))}
        </div>
        {!runner.configured ? (
          <p className="mt-4 text-sm text-state-warning">{t('admin.testSystem.notConfigured')}</p>
        ) : busy ? (
          <p className="mt-4 text-sm text-content-muted">{t('admin.testSystem.busy')}</p>
        ) : null}
        {create.isError ? (
          <p className="mt-4 text-sm text-state-danger">{errorMessage(create.error, t)}</p>
        ) : null}
      </Card>

      {data.active_job ? (
        <JobCard
          title={t('admin.testSystem.job.active')}
          job={data.active_job}
          onCancel={
            data.active_job.status === 'pending'
              ? () => cancel.mutate({ id: data.active_job!.id })
              : undefined
          }
          cancelling={cancel.isPending}
          follow
        />
      ) : null}
      {cancel.isError ? (
        <p className="mt-2 text-sm text-state-danger">{errorMessage(cancel.error, t)}</p>
      ) : null}

      {data.last_finished_job ? (
        <JobCard title={t('admin.testSystem.job.lastFinished')} job={data.last_finished_job} />
      ) : null}

      <History jobs={data.jobs} eventId={eventId} />

      {confirming ? (
        <ConfirmDialog
          kind={confirming}
          prodVersion={runner.prod_version}
          testVersion={runner.test_version}
          sameVersion={sameVersion}
          pending={create.isPending}
          error={create.isError ? errorMessage(create.error, t) : null}
          onCancel={() => setConfirming(null)}
          onConfirm={() =>
            create.mutate({ kind: confirming }, { onSuccess: () => setConfirming(null) })
          }
        />
      ) : null}
    </>
  )
}

// ── État ─────────────────────────────────────────────────────────────

function StatusCard({
  overview,
  sameVersion,
  lastCopy,
}: {
  overview: TestEnvOverview
  sameVersion: boolean
  lastCopy: string | null
}) {
  const { t } = useTranslation()
  const when = useWhen()
  const { runner } = overview

  const runnerState = !runner.configured
    ? { tone: 'warning' as const, text: t('admin.testSystem.status.runnerNotConfigured') }
    : !runner.last_seen_at
      ? { tone: 'warning' as const, text: t('admin.testSystem.status.runnerNever') }
      : runner.online
        ? {
            tone: 'success' as const,
            text: t('admin.testSystem.status.runnerOnline', { when: when(runner.last_seen_at) }),
          }
        : {
            tone: 'danger' as const,
            text: t('admin.testSystem.status.runnerOffline', { when: when(runner.last_seen_at) }),
          }

  const site =
    runner.test_healthy === null
      ? { tone: 'muted' as const, text: t('admin.testSystem.status.unknown') }
      : runner.test_healthy
        ? { tone: 'success' as const, text: t('admin.testSystem.status.reachable') }
        : { tone: 'danger' as const, text: t('admin.testSystem.status.unreachable') }

  return (
    <Card>
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.testSystem.status.title')}
      </h2>
      <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Row label={t('admin.testSystem.status.runner')}>
          <Tone tone={runnerState.tone}>{runnerState.text}</Tone>
        </Row>
        <Row label={t('admin.testSystem.status.testSite')}>
          <Tone tone={site.tone}>{site.text}</Tone>
          {runner.test_url ? (
            <a
              href={runner.test_url}
              target="_blank"
              rel="noreferrer"
              className="ml-2 break-all text-sm text-champagne-300 underline underline-offset-2"
            >
              {t('admin.testSystem.status.open')}
            </a>
          ) : null}
        </Row>
        <Row label={t('admin.testSystem.status.prodVersion')}>
          <Version sha={runner.prod_version} />
        </Row>
        <Row label={t('admin.testSystem.status.testVersion')}>
          <Version sha={runner.test_version} />
          {runner.prod_version && runner.test_version ? (
            <span className="ml-2">
              <Badge tone={sameVersion ? 'success' : 'champagne'}>
                {sameVersion
                  ? t('admin.testSystem.status.sameVersion')
                  : t('admin.testSystem.status.differentVersion')}
              </Badge>
            </span>
          ) : null}
        </Row>
        <Row label={t('admin.testSystem.status.lastCopy')}>
          <span className="text-sm text-content-primary">
            {lastCopy ? when(lastCopy) : t('admin.testSystem.status.never')}
          </span>
        </Row>
      </dl>
    </Card>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-caption uppercase tracking-wide text-content-muted">{label}</dt>
      <dd className="mt-1 flex flex-wrap items-center gap-y-1">{children}</dd>
    </div>
  )
}

function Tone({
  tone,
  children,
}: {
  tone: 'success' | 'warning' | 'danger' | 'muted'
  children: ReactNode
}) {
  const tones = {
    success: 'text-state-success',
    warning: 'text-state-warning',
    danger: 'text-state-danger',
    muted: 'text-content-muted',
  } as const
  return <span className={cn('text-sm', tones[tone])}>{children}</span>
}

function Version({ sha }: { sha: string | null }) {
  const { t } = useTranslation()
  if (!sha) return <span className="text-sm text-content-muted">{t('admin.testSystem.status.unknown')}</span>
  // Sept caractères, comme `git log --oneline` : c'est sous cette forme que
  // l'équipe les voit partout ailleurs. Le commit complet au survol.
  return (
    <code title={sha} className="font-mono text-sm text-content-primary">
      {sha.slice(0, 7)}
    </code>
  )
}

// ── Une demande ──────────────────────────────────────────────────────

function JobCard({
  title,
  job,
  onCancel,
  cancelling = false,
  follow = false,
}: {
  title: string
  job: TestEnvJobDetail
  onCancel?: (() => void) | undefined
  cancelling?: boolean
  follow?: boolean
}) {
  const { t } = useTranslation()

  return (
    <Card className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-heading-md text-content-primary">{title}</h2>
        <StatusBadge job={job} />
      </div>
      <JobFacts job={job} />

      {job.status === 'pending' ? (
        <p className="mt-3 text-sm text-content-secondary">
          {t('admin.testSystem.job.pendingHint')}
        </p>
      ) : null}
      {job.stale ? (
        <p className="mt-3 text-sm text-state-danger">{t('admin.testSystem.job.stale')}</p>
      ) : null}

      <LogTail text={job.log_tail} follow={follow} />

      {onCancel ? (
        <Button
          className="mt-4 w-full sm:w-auto"
          variant="outline"
          size="sm"
          disabled={cancelling}
          onClick={onCancel}
        >
          {t('admin.testSystem.job.cancel')}
        </Button>
      ) : null}
    </Card>
  )
}

function JobFacts({ job }: { job: TestEnvJob }) {
  const { t } = useTranslation()
  const when = useWhen()
  return (
    <ul className="mt-3 grid gap-1 text-sm text-content-secondary">
      <li>
        <span className="text-content-primary">{t(`admin.testSystem.job.kind.${job.kind}`)}</span>
        {' · '}
        {t('admin.testSystem.job.requested', {
          who: job.requested_by_label,
          when: when(job.requested_at),
        })}
      </li>
      {job.started_at ? (
        <li>{t('admin.testSystem.job.started', { when: when(job.started_at) })}</li>
      ) : null}
      {job.finished_at ? (
        <li>{t('admin.testSystem.job.finished', { when: when(job.finished_at) })}</li>
      ) : null}
      {job.prod_version || job.test_version ? (
        <li className="font-mono text-caption">
          {t('admin.testSystem.job.versions', {
            prod: job.prod_version?.slice(0, 7) ?? '—',
            test: job.test_version?.slice(0, 7) ?? '—',
          })}
        </li>
      ) : null}
    </ul>
  )
}

function StatusBadge({ job }: { job: TestEnvJob }) {
  const { t } = useTranslation()
  const tone =
    job.status === 'succeeded'
      ? 'success'
      : job.status === 'failed' || job.stale
        ? 'ember'
        : 'champagne'
  return <Badge tone={tone}>{t(`admin.testSystem.job.status.${job.status}`)}</Badge>
}

function LogTail({ text, follow }: { text: string | null; follow: boolean }) {
  const { t } = useTranslation()
  const box = useRef<HTMLPreElement>(null)

  // Le journal d'une copie en cours se lit par le bas : c'est la dernière
  // ligne qui dit où l'on en est. On y reste à chaque relecture.
  useEffect(() => {
    if (follow && box.current) box.current.scrollTop = box.current.scrollHeight
  }, [text, follow])

  if (!text) {
    return <p className="mt-3 text-sm text-content-muted">{t('admin.testSystem.job.noLog')}</p>
  }
  return (
    <div className="mt-4 min-w-0">
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.testSystem.job.log')}
      </p>
      <pre
        ref={box}
        className="mt-1 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-bg-overlay p-3 font-mono text-caption text-content-secondary"
      >
        {text}
      </pre>
    </div>
  )
}

// ── Historique ───────────────────────────────────────────────────────

function History({ jobs, eventId }: { jobs: TestEnvJob[]; eventId: string | undefined }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState<string | null>(null)

  return (
    <section className="mt-8">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.testSystem.history.title')}
      </h2>
      {jobs.length === 0 ? (
        <p className="mt-3 text-sm text-content-secondary">{t('admin.testSystem.history.empty')}</p>
      ) : (
        <div className="mt-3 grid gap-1.5">
          {jobs.map((job) => (
            <HistoryRow
              key={job.id}
              job={job}
              eventId={eventId}
              open={open === job.id}
              onToggle={() => setOpen(open === job.id ? null : job.id)}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function HistoryRow({
  job,
  eventId,
  open,
  onToggle,
}: {
  job: TestEnvJob
  eventId: string | undefined
  open: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const when = useWhen()
  // Le journal n'est chargé qu'à l'ouverture : vingt journaux de 20 Ko
  // relus toutes les cinq secondes, c'est un demi-mégaoctet pour rien.
  const detail = useAdminQuery<TestEnvJobDetail>(
    [OVERVIEW_KEY, 'job', job.id, job.status],
    `/admin/test-env/jobs/${job.id}`,
    eventId,
    { enabled: open },
  )

  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full px-4 py-3 text-left"
      >
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="shrink-0 text-caption tabular-nums text-content-muted">
            {when(job.requested_at)}
          </span>
          <span className="min-w-0 flex-1 text-sm text-content-primary">
            {t(`admin.testSystem.job.kind.${job.kind}`)}
            <span className="text-content-muted"> · {job.requested_by_label}</span>
          </span>
          <StatusBadge job={job} />
          <span aria-hidden className="shrink-0 text-content-muted">
            {open ? '−' : '+'}
          </span>
        </span>
      </button>
      {open ? (
        <div className="border-t border-line-subtle px-4 pb-4">
          <JobFacts job={job} />
          {detail.isPending ? <Skeleton className="mt-3 h-24 w-full" /> : null}
          {detail.data ? <LogTail text={detail.data.log_tail} follow={false} /> : null}
          {detail.isError ? (
            <p className="mt-3 text-sm text-state-danger">{detail.error.message}</p>
          ) : null}
        </div>
      ) : null}
    </Card>
  )
}

// ── Confirmation ─────────────────────────────────────────────────────

function ConfirmDialog({
  kind,
  prodVersion,
  testVersion,
  sameVersion,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  kind: JobKind
  prodVersion: string | null
  testVersion: string | null
  sameVersion: boolean
  pending: boolean
  error: string | null
  onCancel: () => void
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  const short = (sha: string | null) => sha?.slice(0, 7) ?? '?'

  // Échap referme, comme toute fenêtre : on doit pouvoir renoncer sans
  // chercher le bouton.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !pending) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel, pending])

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center"
      role="dialog"
      aria-modal
      aria-labelledby="test-env-confirm-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-state-danger/40 bg-bg-base p-6">
        <h2
          id="test-env-confirm-title"
          className="font-display text-heading-md text-content-primary"
        >
          {t(`admin.testSystem.confirm.title.${kind}`)}
        </h2>
        <ul className="mt-4 grid list-disc gap-2 pl-5 text-sm text-content-secondary">
          <li className="text-content-primary">{t('admin.testSystem.confirm.lose')}</li>
          {kind === 'refresh' ? (
            <li>{t('admin.testSystem.confirm.refreshCode', { version: short(prodVersion) })}</li>
          ) : (
            <li>{t('admin.testSystem.confirm.resetCode', { version: short(testVersion) })}</li>
          )}
          {kind === 'reset' && !sameVersion && prodVersion && testVersion ? (
            <li className="text-state-warning">{t('admin.testSystem.confirm.resetBehind')}</li>
          ) : null}
          <li>{t('admin.testSystem.confirm.downtime')}</li>
          <li>{t('admin.testSystem.confirm.prodSafe')}</li>
        </ul>
        <p className="mt-4 text-sm font-medium text-content-primary">
          {t('admin.testSystem.confirm.warnTesters')}
        </p>
        {error ? <p className="mt-3 text-sm text-state-danger">{error}</p> : null}
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            {t('admin.testSystem.confirm.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={pending}>
            {t(`admin.testSystem.confirm.go.${kind}`)}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Outils ───────────────────────────────────────────────────────────

function useWhen() {
  const { i18n } = useTranslation()
  return (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
}

/** Les refus connus, dans la langue de l'écran ; les autres, tels que le serveur les dit. */
function errorMessage(error: Error, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'TEST_ENV_JOB_ACTIVE':
        return t('admin.testSystem.errors.active')
      case 'TEST_ENV_RUNNER_NOT_CONFIGURED':
        return t('admin.testSystem.notConfigured')
      case 'TEST_ENV_PRODUCTION_ONLY':
        return t('admin.testSystem.notProductionBody')
      case 'TEST_ENV_JOB_NOT_PENDING':
        return t('admin.testSystem.errors.notPending')
    }
  }
  return error.message
}
